import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const Footer = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    setMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d6o2i2930mftb86r3d3g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setMessage(t('common:footer.subscribeSuccess'));
        setEmail('');
      } else {
        setMessage(t('common:footer.subscribeFailed'));
      }
    } catch (error) {
      setMessage(t('common:footer.subscribeError'));
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      {/* 主要内容区 */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Logo 和简介 */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="https://public.readdy.ai/ai/img_res/bb3a3b5e-bd00-4be3-a469-cabdd447c0ab.png" 
                alt="PDF Tools" 
                className="w-10 h-10"
              />
              <span className="text-xl font-bold">{t('common:siteName')}</span>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              {t('common:footer.description')}
            </p>
          </div>

          {/* 快速链接 */}
          <div>
            <h3 className="text-base font-semibold text-white mb-4">
              {t('common:footer.quickLinks')}
            </h3>
            <ul className="space-y-2">
              <li>
                <a href="/" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">
                  {t('common:nav.home')}
                </a>
              </li>
              <li>
                <a href="/pdf-to-word" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">
                  {t('common:nav.pdfToWord')}
                </a>
              </li>
              <li>
                <a href="/pdf-to-excel" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">
                  {t('common:nav.pdfToExcel')}
                </a>
              </li>
              <li>
                <a href="/pdf-to-ppt" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">
                  {t('common:nav.pdfToPpt')}
                </a>
              </li>
              <li>
                <a href="/image-convert" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">
                  {t('common:nav.imageConvert')}
                </a>
              </li>
              <li>
                <a href="/pdf-tools" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">
                  {t('common:nav.pdfTools')}
                </a>
              </li>
              <li>
                <a href="/tutorials" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">
                  {t('common:nav.tutorials')}
                </a>
              </li>
              <li>
                <a href="/faq" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">
                  {t('common:nav.faq')}
                </a>
              </li>
            </ul>
          </div>

          {/* 关于我们 */}
          <div>
            <h3 className="text-base font-semibold text-white mb-4">
              {t('common:footer.about')}
            </h3>
            <ul className="space-y-2">
              <li>
                <a href="#" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">
                  {t('common:footer.aboutUs')}
                </a>
              </li>
              <li>
                <a href="/tutorials" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">
                  {t('common:footer.tutorials')}
                </a>
              </li>
              <li>
                <a href="/faq" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">
                  {t('common:footer.faq')}
                </a>
              </li>
              <li>
                <a href="#" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer">
                  {t('common:footer.privacyPolicy')}
                </a>
              </li>
            </ul>
          </div>

          {/* 邮件订阅 */}
          <div>
            <h3 className="text-base font-semibold text-white mb-4">
              {t('common:footer.subscribe')}
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              {t('common:footer.subscribeNote')}
            </p>
            <form onSubmit={handleSubscribe} className="space-y-3" data-readdy-form id="newsletter-form">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('common:footer.subscribePlaceholder')}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-sm text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition-colors"
                required
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-medium py-2 px-4 rounded-lg text-sm transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? t('common:footer.subscribing') : t('common:footer.subscribeButton')}
              </button>
              {message && (
                <p className={`text-xs ${message.includes('✓') || message.includes(t('common:footer.subscribeSuccess')) ? 'text-teal-400' : 'text-red-400'}`}>
                  {message}
                </p>
              )}
            </form>
          </div>
        </div>

        {/* 底部广告位 */}
        <div className="mb-8">
          <div className="bg-gray-800 rounded-lg h-24 flex items-center justify-center border-2 border-dashed border-gray-700">
            <p className="text-gray-600 text-sm font-medium">{t('common:footer.adSpace')} 728×90</p>
          </div>
        </div>

        {/* 版权信息 */}
        <div className="border-t border-gray-800 pt-6">
          <p className="text-center text-sm text-gray-500">
            {t('common:footer.copyright')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;